import type { LineStyle, PrimitiveStyleSettings, TextStyle } from "building-types/views/Primitive2DOutput";
import type {
  SheetCircleGeometry,
  SheetGeometry,
  SheetGeometryList,
  SheetPolygonGeometry,
  SheetPolylineGeometry,
  SheetTextGeometry,
} from "building-types/views/sheetGeometryTypes";
import {
  isSheetGeometryLineBased,
  isSheetGeometryTextBased,
  SheetGeometryType,
} from "building-types/views/sheetGeometryTypes";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { numMul } from "common/utils/nominalNumbers";
import { objectMergeCascade } from "common/utils/objectMergeCascade";
import { toLowerCase } from "common/utils/strings";
import type { Mutable } from "common/utils/types";
import { LW_MEDIUM } from "../constants";
import styles, { hatchStyleScale, lineStyleScale, styleSheetCascade, textStyleScale } from "../planStyles";
import type {
  GeometryStyleSheet,
  GeometryStylesInternal,
  SheetGeometryStyleSheet,
  StyleHash,
  TagHash,
  TypeHash,
} from "./primitiveStylesTypes";
import { TAG_DELIMITER, TYPE_DELIMITER } from "./primitiveStylesTypes";

function styleSelector<Delimeter extends string, Name extends string>(
  delimiter: Delimeter,
  name: Name,
): StyleHash<Delimeter, Name> {
  return `${delimiter}${name}`;
}

/**
 * Internal Types are unique, and we don't want accidental hash collisions between
 * any user defined tags with internal styles. In order to prevernt this, we append
 * a unique delimiter to the front of styles for internal types
 */
export function typeSelector<T extends string>(type: T): TypeHash<T> {
  return styleSelector(TYPE_DELIMITER, toLowerCase(type));
}

export function tagSelector<T extends string>(tag: T): TagHash<T> {
  return styleSelector(TAG_DELIMITER, tag);
}

/**
 * Default primitive styles for SheetGeometries. These
 * are different than the global styles defined for primitives,
 * since some parts of the system still rely on non primitives for rendering
 * straight to a target
 */
const defaultPrimitiveStyles: SheetGeometryStyleSheet = {
  line: {
    linewidth: LW_MEDIUM,
  },
};

export function makeGeometryStyleSheet(props: Partial<GeometryStylesInternal>): GeometryStyleSheet {
  return Object.keys(props).reduce((styles, k: string) => {
    styles[typeSelector(k)] = props[k];
    return styles;
  }, {} as Mutable<GeometryStyleSheet>);
}

// This custom application function allows us to properly cascade through undefined values.
// Using just the spread operator can override real values with undefined.
export function applyStylesToGeometry<T extends SheetGeometry>(geometry: T, styles: PrimitiveStyleSettings[]): T {
  return objectMergeCascade<T, PrimitiveStyleSettings>([...styles, geometry]);
}

/**
 * This function is the primary resolution of stylesheets to primitives
 * prior to rendering
 */
export function resolveSheetGeometryStyle<T extends SheetGeometry>(
  geometry: T,
  stylesheet: SheetGeometryStyleSheet = {},
): T {
  return applySheetGeometryStyles(
    geometry,
    styleSheetCascade<SheetGeometryStyleSheet>(styles, defaultPrimitiveStyles, stylesheet),
  );
}

/**
 * This function applies styles to a geometry object, cascading through
 * the stylesheets in order of priority. This function is used to apply
 * styles to a geometry object prior to rendering
 */
export function applySheetGeometryStyles<T extends SheetGeometry>(
  geometry: T,
  stylesheet: SheetGeometryStyleSheet = {},
): T {
  let baseStyle: LineStyle | TextStyle = {};

  if (isSheetGeometryLineBased(geometry)) {
    baseStyle = stylesheet.line ?? {};
  } else if (isSheetGeometryTextBased(geometry)) {
    baseStyle = { ...stylesheet.text };
  }

  if (geometry.type === SheetGeometryType.ASSET || geometry.type === SheetGeometryType.SVG) {
    return geometry;
  }

  const geometryStyle = selectSheetGeometryStyle(stylesheet, geometry.type) ?? {};
  const tags = geometry.tags ?? [];
  const tagStyle = selectSheetTagStyle(stylesheet, tags) ?? {};

  const styledGeometry = applyStylesToGeometry(geometry, [baseStyle, geometryStyle, tagStyle]);
  return styledGeometry;
}

export function selectSheetGeometryStyle<T extends keyof GeometryStylesInternal>(
  stylesheet: SheetGeometryStyleSheet,
  geometryType: T,
): GeometryStylesInternal[T] {
  return stylesheet[typeSelector(geometryType)] ?? {};
}

export function selectSheetTagStyle<T extends string>(
  stylesheet: SheetGeometryStyleSheet,
  tags: T[],
): PrimitiveStyleSettings {
  let style: PrimitiveStyleSettings = {};
  for (const tag of tags) {
    const tagStyle = stylesheet[tagSelector(tag)];
    style = { ...style, ...tagStyle };
  }
  return style;
}

export interface SheetGeometryScaleStyleOptions {
  includeTextStyles?: boolean;
  includeBoxStyles?: boolean;
  includeLineStyles?: boolean;
  includeHatchStyles?: boolean;
}

/**
 * This function is a bit clunky because styles for primitives are not
 * distinguished from the primitive geometry itself. This will
 * become easier to manage after styles are a separate typed property of the primitive
 */
export function sheetGeometryScaleStyle<T extends SheetGeometry>(
  geom: T,
  scale: number,
  options?: SheetGeometryScaleStyleOptions,
): T {
  if (scale === 1) {
    return geom;
  }

  const {
    includeTextStyles = true,
    includeBoxStyles = true,
    includeLineStyles = false,
    includeHatchStyles = false,
  } = options ?? {};

  switch (geom.type) {
    case SheetGeometryType.TEXT: {
      let text: SheetTextGeometry = includeTextStyles ? textStyleScale<SheetTextGeometry>(geom, scale) : geom;
      if (includeBoxStyles) {
        const boxWidth = text.boxWidth ? numMul(text.boxWidth, scale) : undefined;
        const boxHeight = text.boxHeight ? numMul(text.boxHeight, scale) : undefined;
        // Sheet inches like boxWidth/boxHeight, so they convert to plan space here rather than being
        // baked into x/y at generation time, where the view scale is unknown.
        const offsetX = text.offsetX ? numMul(text.offsetX, scale) : undefined;
        const offsetY = text.offsetY ? numMul(text.offsetY, scale) : undefined;
        text = { ...text, boxWidth, boxHeight, offsetX, offsetY };
      }
      return text as T;
    }
    case SheetGeometryType.ASSET: {
      return geom;
    }
    case SheetGeometryType.SVG: {
      return geom;
    }
    case SheetGeometryType.POLYLINE: {
      let pl: SheetPolylineGeometry = includeLineStyles ? lineStyleScale<SheetPolylineGeometry>(geom, scale) : geom;
      pl = includeHatchStyles ? hatchStyleScale<SheetPolylineGeometry>(pl, scale) : pl;
      return pl as T;
    }

    case SheetGeometryType.POLYGON: {
      let pl: SheetPolygonGeometry = includeLineStyles ? lineStyleScale<SheetPolygonGeometry>(geom, scale) : geom;
      pl = includeHatchStyles ? hatchStyleScale<SheetPolygonGeometry>(pl, scale) : pl;
      return pl as T;
    }
    case SheetGeometryType.CIRCLE: {
      const pl: SheetCircleGeometry = includeLineStyles ? lineStyleScale<SheetCircleGeometry>(geom, scale) : geom;
      return pl as T;
    }
    case SheetGeometryType.ARC: {
      return geom;
    }
    default: {
      assertUnreachable(geom);
    }
  }
}

export function sheetGeometryStylePass<T extends SheetGeometry>(
  geometry: T,
  stylesheet?: SheetGeometryStyleSheet,
  textScaleFactorMultiplierOverride?: number,
): T {
  let processedGeometry = resolveSheetGeometryStyle(geometry, stylesheet);

  if (textScaleFactorMultiplierOverride !== undefined) {
    processedGeometry = sheetGeometryScaleStyle(processedGeometry, textScaleFactorMultiplierOverride);
  }

  return processedGeometry;
}

export function applyTextGeometryDefaultFontSize(
  geometryList: SheetGeometryList,
  fontSize: SheetInches,
): SheetGeometryList {
  const geometry = geometryList.geometry.map((geo) => {
    if (geo.type !== SheetGeometryType.TEXT) {
      return geo;
    }
    const updatedGeo = geo.fontSize === undefined ? { ...geo, fontSize } : geo;
    return resolveSheetGeometryStyle(updatedGeo);
  });

  return { geometry };
}
